import Html from './html';
import Video from './video';
import AppOptions from '../app/options';

declare var Splide: any;

class Stories {

    private slides: Array<HTMLElement>;
    private currentVideoIndex: number = 0;
    private currentVCIndex: number = 0;
    private hasVerticalPreroll: boolean;
    private videoPlayCount: number = 0;

    private storiesSplide: any;

    constructor(
        private html: Html,
        private video: Video,
        options: AppOptions,
    ) {
        this.slides = this.html.qAll('.vc-slide');
        this.hasVerticalPreroll = Boolean(options.ads?.verticalPrerollUrl);
    }

    init(): void {
        const url = new URL(window.location.href);
        const parts = url.pathname.replace(/^\/|\/$/g, '').split('/');

        const watchedStories = JSON.parse(localStorage.getItem('watchedStories') || '{}');

        const track = this.html.q('#stories_slider .splide__list');

        if (!track) {
            return;
        }
        
        track.innerHTML = '';
        
        const slidesWithInfo = this.slides.map((slide, i) => {
            if (!slide.dataset.id) {
                return;
            }

            let isFirst = false;

            const vcData = this.getVCData(i);

            if (vcData && vcData.Url === '/' + parts[1]) {
                isFirst = true;
            }

            const storyId = +slide.dataset.id;
            const lastWatched = watchedStories[storyId] ? watchedStories[storyId].lastWatchedId : 0;
            const lastVideoId = vcData.Medias[vcData.Medias.length - 1].Id;
            const isWatched = lastVideoId > 0 && lastVideoId === lastWatched;

            return { slide, isWatched, isFirst };
        });

        slidesWithInfo.sort((a, b) => {
            if (!a || !b) {
                return 0;
            }

            if (a.isFirst && !b.isFirst) {
                return -1;
            }
            if (!a.isFirst && b.isFirst) {
                return 1;
            }

            if (a.isWatched && !b.isWatched) {
                return 1;
            }
            if (!a.isWatched && b.isWatched) {
                return -1;
            }

            return 0;
        });

        slidesWithInfo.forEach((swi, i) => {
            let el = swi?.slide as HTMLElement;
            el.dataset.idx = i.toString();

            track.appendChild(el);
        });

        this.slides = this.html.qAll('.vc-slide');

        if (!this.slides.length) {
            return;
        }

        const vcData = this.getVCData(0);
        if (!vcData) {
            return;
        }

        this.currentVideoIndex = this.getCurrentVideoIndex(vcData);

        this.storiesSplide = new Splide('#stories_slider', {
            autoplay: false,
            arrows: false,
            pagination: false,
            perPage: 1,
            perMove: 1,
            focus: 'center',
            fixedWidth: '100vw'
        });

        this.storiesSplide.on('mounted', () => {
            this.playVideo(true);
        });

        this.storiesSplide.on('move', (newIndex: number) => {
            const vcData = this.getVCData(newIndex);
            if (!vcData) {
                return;
            }

            this.currentVCIndex = newIndex;
            this.currentVideoIndex = this.getCurrentVideoIndex(vcData);

            this.playVideo();
        });

        this.storiesSplide.mount();

        const storyPrev = this.html.q('#story_prev');
        const videoPrev = this.html.q('#video_prev');
        const storyNext = this.html.q('#story_next');
        const videoNext = this.html.q('#video_next');


        if (storyNext && videoNext && storyPrev && videoPrev) {
            storyNext.addEventListener('click', () => this.handleClick(0, this.currentVCIndex + 1));
            videoNext.addEventListener('click', () => this.handleClick(this.currentVideoIndex + 1, this.currentVCIndex));
            storyPrev.addEventListener('click', () => this.handleClick(0, this.currentVCIndex - 1));
            videoPrev.addEventListener('click', () => this.handleClick(this.currentVideoIndex - 1, this.currentVCIndex));
        }

        document.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
                this.handleClick(this.currentVideoIndex - 1, this.currentVCIndex);
            }
            
            if (e.key === 'ArrowRight') {
                this.handleClick(this.currentVideoIndex + 1, this.currentVCIndex);
            }
        });

        const ref = document.referrer && new URL(document.referrer);

        if (ref && ref.origin === window.location.origin && ref.pathname === "/") {
            this.html.q("#back-button")?.setAttribute("href", "/#stories");
        }
    }

    private getCurrentVideoIndex(vcData: any): number {
        let savedProgress = this.getSavedProgress(vcData.Id);

        let indexSaved = vcData.Medias.findIndex((m: any) => m.Id === savedProgress);

        if (indexSaved < 0) {
            // If user has not seen any video, start at first
            return 0;
        } else if (indexSaved >= vcData.Medias.length - 1) {
            // If user has seen all videos, go to first one
            return 0;
        } else {
            // Show next video of the one user has seen last
            return indexSaved + 1;
        }
    }

    private handleClick(videoIndex: number, vcIndex: number): void {
        const vcData = this.getVCData(vcIndex);
        if (!vcData) {
            return;
        }

        if (videoIndex+1 > vcData.Medias.length) {
            vcIndex = vcIndex+1 > this.slides.length ? vcIndex : vcIndex + 1;
            videoIndex = 0;
        }

        if (videoIndex < 0) {
            vcIndex = vcIndex > 0 ? vcIndex - 1 : vcIndex;
            videoIndex = 0;
        }

        if (this.currentVCIndex === vcIndex) {
            this.currentVideoIndex = videoIndex;
            this.playVideo();

            return;
        }

        if (vcIndex > this.currentVCIndex) {
            this.storiesSplide.go('>');
        }

        if (vcIndex < this.currentVCIndex) {
            this.storiesSplide.go('<');
        }
    }

    private getVCData(vcIndex: number): any {
        const videoWrapperEl = this.slides[vcIndex]?.querySelector('[id^="stories-player"]') as HTMLDivElement | null;

        if (!videoWrapperEl || !videoWrapperEl.dataset.vc) {
            return;
        }

        return JSON.parse(videoWrapperEl.dataset.vc);
    }

    private playVideo(mute = false): void {
        const vcData = this.getVCData(this.currentVCIndex);

        if (!vcData) {
            return;
        }
   
        const playerDiv = `#stories-player-${vcData.Id}`;
        const videoData = {Id: vcData.Medias[this.currentVideoIndex].Id};
        const currentSlide = this.slides[this.currentVCIndex];
        this.setAdUi(currentSlide, false);
        
        this.updateProgressBar(vcData);
        this.setReadMoreUrl(vcData.Medias[this.currentVideoIndex], vcData.Id);
        const playbackOptions = {
            disableDblClick: true,
            autoplay: true,
            mute: mute,
            focus: false,
            replaceUrl: true,
            customUrl: '/video-zgodbe' + vcData.Url,
            showPoster: false,
            showVerticalVideoAd: this.shouldPlayPreroll(),
            isVerticalVideo: true,
            onAdStarted: () => this.setAdUi(currentSlide, true),
            onAdCompleted: () => this.setAdUi(currentSlide, false)
        };

        this.video.destroyPlayers();
        this.video
            .play(playerDiv, videoData, playbackOptions)
            .catch(result => this.handlePlaybackInterruption(result))
            .catch(result => {
                console.log('Stories', result?.err);

                result?.player?.dispose();
                playbackOptions.mute = true;
                return this.video.play(playerDiv, videoData, playbackOptions)
                    .catch(result => this.handlePlaybackInterruption(result));
            })
            .then(player => {
                if (!player || player.isDisposed()) {
                    return;
                }
                const customBigPlayButton = (<any>window).videojs.getComponent('CustomBigPlayButton');
                if (!customBigPlayButton) {
                    this.registerCustomBigButton();
                }

                player.on('timeupdate', (e: Event) => { this.onPlayerTimeupdate(player, vcData); });
                player.on('ended',      (e: Event) => { this.onVideoEnded(player); });
                player.on('play',       (e: Event) => { this.onPlayerPlay(player, vcData); });

                // Trigger onPlayerPLay() because video has already started playing before
                // we attached handler.
                this.onPlayerPlay(player, vcData);

                player.addChild('CustomBigPlayButton', {});
            })
            .catch(result => {
                console.log('Stories', result);
            });

        let savedProgress = this.getSavedProgress(vcData.Id);

        let indexSaved = vcData.Medias.findIndex((m: any) => m.Id === savedProgress);

        // Save index position for this story. If user comes back to this story,
        // we'll start showing him video he has not yet seen.
        // If user has seen all videos we show him the first video again. We must
        // save index 0 or he will always start get first video in this story.
        if (this.currentVideoIndex > indexSaved || this.currentVideoIndex === 0) {
            this.saveProgress(vcData.Id, vcData.Medias[this.currentVideoIndex].Id);
        }

        this.handleArrows(this.currentVCIndex, this.slides.length);
    }

    private handlePlaybackInterruption(result: any): any {
        if (result?.player?.isDisposed()) {
            return;
        }
        if (result?.err?.name === 'AbortError' && result?.player?.ads?.isInAdMode?.()) {
            return result.player;
        }
        throw result;
    }

    private registerCustomBigButton(): void {
        if (!(<any>window).videojs) {
            return;
        }

        const BigPlayButton = (<any>window).videojs.getComponent('BigPlayButton');

        class CustomBigPlayButton extends BigPlayButton {
            private touchHandled = false;

            constructor(player: any, options: any) {
                super(player, options);

                this.on('touchend', (e: TouchEvent) => this.handleTouchStart(e));
            }

            handleTouchStart(event: TouchEvent): void {
                const touch = event.touches[0] || event.changedTouches[0];

                if (!event.target) {
                    return;
                }

                this.touchHandled = true;
                this.handleMove(event.target, touch.clientX);
            }

            handleClick(event: MouseEvent): void {
                // If we haev a tap (touch) event on mobile, clientX is undefined. We
                // can exit here, touch event is handled in handleTouchStart().
                if (typeof event.clientX === 'undefined' || !event.target) {
                    return;
                }

                // Browsers fire touchstart and then click handlers. We must process only
                // touchStart and skip click.
                if (this.touchHandled) {
                    this.touchHandled = false;
                    return;
                }

                this.handleMove(event.target, event.clientX);
            }

            private handleMove(target: EventTarget, clientX: number): void {
                const rect = (target as HTMLElement).getBoundingClientRect();
                const clickX = clientX - rect.left; // x relative to element
                const half = rect.width / 2;

                const side = clickX < half ? 'left' : 'right';

                (<any>window).app.stories.onBigButtonClick(side);
            }
        }

        (<any>window).videojs.registerComponent('CustomBigPlayButton', CustomBigPlayButton);
    }

    onBigButtonClick(side: string) {
        if (side === 'right') {
            this.handleClick(this.currentVideoIndex + 1, this.currentVCIndex);
        } else {
            this.handleClick(this.currentVideoIndex - 1, this.currentVCIndex);
        }
    }

    private onVideoEnded(player: any) {
        this.handleClick(this.currentVideoIndex + 1, this.currentVCIndex);
    }

    private onPlayerPlay(player: any, vcData: any): void {
        if (player.paused()) {
            return;
        }

        this.updateProgressBar(vcData);
    }

    private updateProgressBar(vcData: any): void {
        let found = false;

        this.html.qAll('[class^="progress-bar-' + vcData.Id + '"]').forEach(el => {
            if (!el.dataset.id) {
                return;
            }

            if (found) {
                el.classList.remove('story-watched');
                el.querySelector('span')?.style.removeProperty("width");
                return;
            }

            if (+el.dataset.id !== vcData.Medias[this.currentVideoIndex].Id) {
                el.classList.add('story-watched');
                el.querySelector('span')?.style.removeProperty("width");
            } else {
                el.classList.remove('story-watched');
                el.querySelector('span')?.style.removeProperty("width");
                found = true;
            }
        });
    }

    private setAdUi(slide: HTMLElement | undefined, isAdPlaying: boolean): void {
        if (!slide) {
            return;
        }

        this.html.toggleClassIf(this.html.qAll('.video-ad-label', slide), !isAdPlaying, 'hidden');
        this.html.toggleClassIf(this.html.qAll('.story-info', slide), isAdPlaying, 'hidden');
        this.html.toggleClassIf(this.html.qAll('.stories-progressbar', slide), isAdPlaying, 'hidden');
    }

    private shouldPlayPreroll(): boolean {
        this.videoPlayCount += 1;

        return this.hasVerticalPreroll
            && this.videoPlayCount >= 3
            && (this.videoPlayCount - 3) % 5 === 0;
    }

    private onPlayerTimeupdate(player: any, vcData: any): void {
        const el: HTMLElement | null = this.html.q('.progress-bar-' + vcData.Id + '-' + vcData.Medias[this.currentVideoIndex].Id + ' span');

        if (!el) {
            return;
        }

        const current = player.currentTime();
        const duration = player.duration();

        if (!duration || duration === Infinity) {
            return;
        }

        const percent = (current / duration) * 100;

        el.style.width = percent + '%';
    }

    private saveProgress(vcId: string, videoId: number): void {
        const watched = JSON.parse(localStorage.getItem('watchedStories') || '{}');
        watched[vcId] = { lastWatchedId: videoId };
        localStorage.setItem('watchedStories', JSON.stringify(watched));
    }

    private getSavedProgress(vcId: string): number {
        const watched = JSON.parse(localStorage.getItem('watchedStories') || '{}');
        return watched[vcId]?.lastWatchedId ?? 0;
    }

    private handleArrows(index: number, allSlides: number): void {
        if (index > 0) {
            this.html.removeClass('#story_prev', 'button-disabled');
        } else {
            this.html.addClass('#story_prev', 'button-disabled');
        }

        if (index < allSlides - 1) {
            this.html.removeClass('#story_next', 'button-disabled');
        } else {
            this.html.addClass('#story_next', 'button-disabled');
        }
    }


    private setReadMoreUrl(vcData: any, vcId: number): void {
        const readMoreContainer = this.html.q('#read-more-container-' + vcId) as HTMLDivElement | null;
        const readMoreButton = this.html.q('#read-more-button-' + vcId) as HTMLAnchorElement | null;

        if (readMoreContainer){
            if (vcData.ReadMoreUrl) {
                readMoreContainer.style.display = 'flex';
            } else {
                readMoreContainer.style.display = 'none';
            }
        }
        if (readMoreButton) {
            readMoreButton.href = vcData.ReadMoreUrl;
            readMoreButton.innerHTML = vcData.ReadMoreText;
        }

    }

   
}

export default Stories;
